import { EntitiesRights } from '../entities/EntitiesRights.svelte';
import { UsersRights } from '../users/UsersRights';
import { UsersTexts } from '../users/UsersTexts';
import { CommonTexts } from '../../services/CommonTexts';
import { MenuService } from '../../services/MenuService.svelte';
import { PageService } from '../../services/PageService.svelte';
import type { IconType } from '../../components/icons/icons.types';
import type { SearchHitData, SearchInfoData } from '../../components/values/values.types';
import type { SearchAnswerBody } from '../../api/types/search.types';
import type { PageKey } from '../../services/page.types';

/** One line of the search page, whatever module it comes from. */
export interface SearchEntry {
    key: string;
    icon: IconType;
    area: string;
    hit: SearchHitData;
    info: SearchInfoData;
    href: string;
    /** Where choosing it leads: the details page, the list with the search without one. */
    target: string;
    quality: number;
    /** Below the minimum quality, shown only on the development server. */
    isBelow: boolean;
    /** The card of the record, if its module has one and the user may see it. */
    card?: SearchCard;
}

/** Which card opens, for which record. */
export interface SearchCard {
    kind: 'person' | 'organization' | 'user' | 'team';
    id: number;
    /** The name of the card, as in the module's list. */
    label: string;
}

type SearchEntryBase = Pick<SearchEntry, 'hit' | 'href' | 'card'> & { page: PageKey; details: PageKey; info: Omit<SearchInfoData, 'fields'> };

/** Turns the rows of every module into the lines of the search page, in the order of the API. */
class SearchEntriesClass {
    public list(body: SearchAnswerBody, query: string): SearchEntry[] {
        const Bases = this.listBases(body);
        const Entries: SearchEntry[] = [];
        for (const Result of body.results) {
            const Key = this.getKey(Result.module, Result.primary);
            const Base = Bases.get(Key);
            if (Base !== undefined) {
                const Page = PageService.getPage(Base.page);
                const Target = PageService.canAccess(Base.details) ? Base.href : MenuService.getListSearchHref(Base.page, query);
                Entries.push({
                    ...Base,
                    key: Key,
                    target: Target,
                    icon: Page.icon,
                    area: Page.label,
                    info: { ...Base.info, fields: Result.fields, isBelow: Result.is_below },
                    quality: Result.quality,
                    isBelow: Result.is_below,
                });
            }
        }

        return Entries;
    }

    private getKey(module: string, primary: number | string): string {
        return `${module}:${primary.toString()}`;
    }

    private listBases(body: SearchAnswerBody): Map<string, SearchEntryBase> {
        const Bases = new Map<string, SearchEntryBase>();
        for (const Row of body.rows.persons ?? []) {
            Bases.set(this.getKey('persons', Row.person_id), {
                page: 'persons',
                details: 'person',
                hit: { person: Row },
                info: { email: Row.email },
                href: `/persons/${Row.person_id.toString()}`,
                card: EntitiesRights.canSeePersonCard() ? { kind: 'person', id: Row.person_id, label: CommonTexts.Card } : undefined,
            });
        }
        for (const Row of body.rows.organizations ?? []) {
            Bases.set(this.getKey('organizations', Row.organization_id), {
                page: 'organizations',
                details: 'organization',
                hit: { text: Row.organization },
                info: { email: Row.email },
                href: `/organizations/${Row.organization_id.toString()}`,
                card: EntitiesRights.canSeeOrganizationCard() ? { kind: 'organization', id: Row.organization_id, label: CommonTexts.Card } : undefined,
            });
        }
        for (const { user, teams } of body.rows.users ?? []) {
            Bases.set(this.getKey('users', user.user_id), {
                page: 'users',
                details: 'admin_user',
                hit: { user },
                info: { teams: teams ?? [] },
                href: `/users/${user.user_id.toString()}`,
                card: UsersRights.canSeeUserCard() ? { kind: 'user', id: user.user_id, label: UsersTexts.UserCard } : undefined,
            });
        }
        for (const Row of body.rows.teams ?? []) {
            Bases.set(this.getKey('teams', Row.team_id), {
                page: 'teams',
                details: 'admin_team',
                hit: { team: Row },
                info: {},
                href: `/teams/${Row.team_id.toString()}`,
                card: UsersRights.canSeeTeamCard() ? { kind: 'team', id: Row.team_id, label: UsersTexts.TeamCard } : undefined,
            });
        }
        for (const Row of body.rows.tags ?? []) {
            Bases.set(this.getKey('tags', Row.tag_inx), { page: 'tags', details: 'tag', hit: { tag: Row.tag }, info: {}, href: `/tags/${encodeURIComponent(Row.tag_inx)}` });
        }
        for (const Row of body.rows.positions ?? []) {
            Bases.set(this.getKey('positions', Row.position_id), {
                page: 'positions',
                details: 'position',
                hit: { text: Row.position },
                info: {},
                href: `/positions/${Row.position_id.toString()}`,
            });
        }

        return Bases;
    }
}

export const SearchEntries = new SearchEntriesClass();
